/**
 * Ski & Sport Shack — "Notify me when it's back" (blocks/sss-notify-me.liquid).
 *
 * Follows Horizon's variant selection (`shopify:product:select`, the same event product-sku.js and
 * product-inventory.js use) and, from the selected variant's JSON (`detail.resource`):
 * - shows the block while the variant is sold out, hides it while it is purchasable or when the
 *   option combination does not exist (resource null);
 * - rewrites the variant line and the hidden contact fields (variant title, SKU, id, URL, body);
 * - after a successful post, the success note belongs to the posted variant only: picking another
 *   sold-out variant brings the form back (the typed email stays, nothing is re-rendered).
 * The form itself posts natively (no fetch) so Shopify's contact spam protection keeps working.
 */
import { StandardEvents } from '@shopify/events';

class SssNotifyMe extends HTMLElement {
  /** @type {Element | null} */
  #target = null;

  connectedCallback() {
    this.#target = this.closest('.shopify-section, dialog');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#target = null;
  }

  /** @param {Event & { promise?: Promise<{ detail?: any }> }} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail) return;
        if (detail.newProduct) return; // combined listing: the section morph re-renders this block
        if (detail.productId && detail.productId !== this.dataset.productId) return;
        this.#update(detail.resource ?? null);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sss-notify-me] Event promise rejected:', error);
      });
  };

  /** @param {{ id: number, title: string, sku?: string, available: boolean } | null} variant */
  #update(variant) {
    if (!variant || variant.available !== false) {
      this.hidden = true;
      return;
    }

    const isDefault = this.dataset.defaultVariant === 'true';
    const label = isDefault ? '' : variant.title || '';
    const url = `${this.dataset.productUrl}?variant=${variant.id}`;

    this.#setField('variant', variant.title || '');
    this.#setField('sku', variant.sku || '');
    this.#setField('id', String(variant.id));
    this.#setField('url', url);

    const body = this.querySelector('[data-field="body"]');
    if (body instanceof HTMLInputElement) {
      const title = body.dataset.productTitle || '';
      body.value = `Please email me when ${title}${label ? ` (${label})` : ''} is back in stock.`;
    }

    const variantLabel = this.querySelector('[data-variant-label]');
    if (variantLabel) variantLabel.textContent = label;
    this.querySelector('[data-variant-line]')?.toggleAttribute('hidden', !label);
    this.querySelector('[data-product-line]')?.toggleAttribute('hidden', Boolean(label));

    const success = /** @type {HTMLElement | null} */ (this.querySelector('[data-success]'));
    const fields = /** @type {HTMLElement | null} */ (this.querySelector('[data-fields]'));
    const postedFor = success?.dataset.postedVariant;
    const showSuccess = Boolean(postedFor) && postedFor === String(variant.id);
    if (success) success.hidden = !showSuccess;
    if (fields) fields.hidden = showSuccess;

    this.hidden = false;
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  #setField(name, value) {
    const input = this.querySelector(`[data-field="${name}"]`);
    if (input instanceof HTMLInputElement) input.value = value;
  }
}

if (!customElements.get('sss-notify-me')) {
  customElements.define('sss-notify-me', SssNotifyMe);
}
